import * as Sentry from "@sentry/vue";
import { ref, computed, readonly } from "vue";
import {
  buildOrganizationHierarchy,
  linkScopeAncestors,
  collapseToBranchPoint,
} from "../dashboard/helper";

// The organizations the logged-in user can actually navigate — the UNION of
// every subtree under an org where they hold an active role (a region expands to
// its facilities), served by the backend `myOrganizations` query. This replaces
// the old assumption that navigation scope == descendants of the single
// `defaultOrganization`, which hid every scope but the first for multi-org
// managers. Module-level singleton, shared across all callers.

const STORE_SCOPE = "accessibleOrganizations";

const STORE_TOP_LEVEL = "topLevelOrganization";

const accessibleOrganizations = ref(restore());

// The highest platform/application organization the user holds a role on, if
// any. Such a role grants navigation of the whole tree under that org, which the
// bounded scope union below deliberately omits (expanding the entire system into
// the login payload is too large). For these admins the app roots navigation at
// this org and searches the server, exactly as it does for a user whose default
// organization is itself platform/application. Persisted so it survives reloads
// alongside the scope. Platform outranks application when the user holds both.
const topLevelOrganization = ref(restoreTopLevel());

const accessibleFacilities = computed(() =>
  accessibleOrganizations.value.filter((o) => o.type === "facility")
);
const accessibleRegions = computed(() =>
  accessibleOrganizations.value.filter((o) => o.type === "region")
);

// A user with a platform/application role can navigate every organization,
// region and facility beneath that org, even when they also hold lower
// region/facility roles that would otherwise bound them to a subset.
const hasSystemScope = computed(() => !!topLevelOrganization.value);

// A user reaches multiple facilities when they hold a system-wide role, can
// navigate to more than one facility, or hold any region (a region always
// implies a broader-than-single-facility view). Such users land on the
// management summary with a facility switcher rather than being dropped into a
// single facility. This gates the header switcher, the management-summary
// landing, and facility seeding.
const reachesMultipleFacilities = computed(
  () =>
    hasSystemScope.value ||
    accessibleFacilities.value.length > 1 ||
    accessibleRegions.value.length > 0
);

// A map of every node in scope plus its ancestor path, keyed by id, with parentId
// reconstructed from the ordered `ancestors` array (see linkScopeAncestors).
const scopeNodesById = computed(() => {
  const byId = new Map();
  linkScopeAncestors(accessibleOrganizations.value).forEach((node) => {
    byId.set(node.id, node);
  });
  return byId;
});

// The top-most region a node belongs to, found by walking the parent chain and
// keeping the highest region-type node seen. A node nested region → sub-region →
// facility resolves to its top region, so a single region with sub-regions reads
// as ONE region rather than several. Returns null when no region is on the chain
// (a node directly under an organization, or one whose region is not in scope).
function topRegionId(node, nodesById) {
  const seen = new Set();
  let current = node;
  let top = current?.type === "region" ? current.id : null;
  while (current?.parentId && !seen.has(current.parentId)) {
    seen.add(current.parentId);
    const parent = nodesById.get(current.parentId);
    if (!parent) break;
    if (parent.type === "region") top = parent.id;
    current = parent;
  }
  return top;
}

// A user has cross-region scope when their permitted scope spans more than one
// top-level region; distinct region buckets > 1 means the tree would branch
// across regions. Both directly-permitted regions AND every permitted facility
// contribute a bucket, so a manager granted a whole region PLUS individual
// facilities in another region counts as cross-region even when the region has
// no facilities of its own in scope. A node's bucket is its top region when that
// region is in scope, otherwise its immediate `parentId` (its region/sub-region)
// — the always-present parent still separates disjoint facilities whose region
// node and `ancestors` have not loaded.
const hasCrossOrganizationScope = computed(() => {
  const nodesById = scopeNodesById.value;
  const regionKeys = new Set();
  const addBucket = (node) => {
    const key = topRegionId(node, nodesById) || node.parentId;
    if (key) regionKeys.add(key);
  };
  accessibleRegions.value.forEach(addBucket);
  accessibleFacilities.value.forEach(addBucket);
  return regionKeys.size > 1;
});

// The permitted scope as a hierarchy rooted at the lowest common ancestor. Every
// node is linked by its reconstructed parentId, then single-child ancestor levels
// above the branch point are dropped so the root is the meaningful grouping
// level. Nodes are cloned before building because buildOrganizationHierarchy
// mutates `.children`.
const accessibleOrgTree = computed(() => {
  const clones = Array.from(scopeNodesById.value.values()).map((node) => ({
    ...node,
    children: [],
  }));
  return buildOrganizationHierarchy(clones).map(collapseToBranchPoint);
});

// The top of the permitted scope: the single root of `accessibleOrgTree`, which
// collapsing has already reduced to the level where the scope branches (several
// regions resolve to the organization above them). Null when the scope has more
// than one root, or when that root is a platform/application node sitting above
// the customer tree, since neither is an organization record to navigate to.
const scopeRootOrganization = computed(() => {
  const roots = accessibleOrgTree.value;
  if (roots.length !== 1) return null;

  const [root] = roots;
  return ["organization", "region", "facility"].includes(root?.type)
    ? root
    : null;
});

// The one facility to enter directly at login — only when the user's whole
// navigable scope is exactly one facility (no regions).
const singleFacilityScope = computed(() =>
  !reachesMultipleFacilities.value && accessibleFacilities.value.length === 1
    ? accessibleFacilities.value[0]
    : null
);

function setAccessibleOrganizations(orgs) {
  accessibleOrganizations.value = Array.isArray(orgs) ? orgs : [];

  try {
    window.localStorage.setItem(
      STORE_SCOPE,
      JSON.stringify(accessibleOrganizations.value)
    );
  } catch (error) {
    // Storage can refuse the write: quota exceeded (the payload now carries
    // every role's ancestors and descendants) or Safari private mode. The scope
    // this session reads is the ref set above, so a refused write costs a
    // re-derive on reload rather than the sign-in itself.
    Sentry.captureException(error);
  }
}

function setTopLevelOrganization(org) {
  topLevelOrganization.value = org || null;

  try {
    if (org) {
      window.localStorage.setItem(STORE_TOP_LEVEL, JSON.stringify(org));
    } else {
      window.localStorage.removeItem(STORE_TOP_LEVEL);
    }
  } catch (error) {
    Sentry.captureException(error);
  }
}

// Build the navigable scope from the logged-in user's roles instead of a separate
// `myOrganizations` query. The login response carries, per role, the org (with its
// ancestor path) and its descendant sub-tree (a region expanded to its facilities),
// so the same union `myOrganizations` produced is assembled here from the stored
// user — no round-trip, and it survives reloads. Platform/application role orgs
// carry no descendants (bounded server-side), so those admins contribute nothing
// and fall back to the rooted tree.
// Platform sits above application in the tree, so it wins when a user holds both.
const TOP_LEVEL_RANK = { application: 1, platform: 2 };

function deriveAccessibleScope(user) {
  const byId = new Map();
  let topLevel = null;
  (user?.roles || []).forEach((assignment) => {
    const org = assignment?.organization;
    if (!org?.id) return;

    // A platform/application role grants the whole sub-tree under that org. The
    // login payload carries no descendants for it (bounded server-side), so it
    // adds nothing to the bounded union; record the org itself as the navigation
    // root for a system-scoped admin instead.
    if (["platform", "application"].includes(org.type)) {
      const higher =
        !topLevel || TOP_LEVEL_RANK[org.type] > TOP_LEVEL_RANK[topLevel.type];
      if (higher) {
        const { descendants: _descendants, ...node } = org;
        topLevel = node;
      }
      return;
    }

    if (!["organization", "region", "facility"].includes(org.type)) return;

    // Store the org without its nested `descendants` (they're flattened below),
    // keeping its `ancestors` so the tree can place it under its full path.
    const { descendants, ...orgNode } = org;
    if (!byId.has(orgNode.id)) byId.set(orgNode.id, orgNode);
    (descendants || []).forEach((node) => {
      if (node?.id && !byId.has(node.id)) byId.set(node.id, node);
    });
  });
  setTopLevelOrganization(topLevel);
  setAccessibleOrganizations(Array.from(byId.values()));
  return accessibleOrganizations.value;
}

function restore() {
  try {
    const stored = window.localStorage.getItem(STORE_SCOPE);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

function restoreTopLevel() {
  try {
    const stored = window.localStorage.getItem(STORE_TOP_LEVEL);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

function clearAccessibleScope() {
  window.localStorage.removeItem(STORE_SCOPE);
  window.localStorage.removeItem(STORE_TOP_LEVEL);
  accessibleOrganizations.value = [];
  topLevelOrganization.value = null;
}

function useAccessibleScope() {
  return {
    accessibleOrganizations: readonly(accessibleOrganizations),
    accessibleFacilities,
    accessibleRegions,
    reachesMultipleFacilities,
    hasCrossOrganizationScope,
    hasSystemScope,
    topLevelOrganization: readonly(topLevelOrganization),
    accessibleOrgTree,
    scopeRootOrganization,
    singleFacilityScope,
    deriveAccessibleScope,
    clearAccessibleScope,
  };
}

export default useAccessibleScope;
export { deriveAccessibleScope, clearAccessibleScope };
